import { ArrowTopRightOnSquareIcon, EyeIcon, MinusCircleIcon } from "@heroicons/react/24/solid";
import { Character } from "@moe/priv/model/character";
import { chatConfig } from "@moe/priv/model/chat";
import { useNavigate } from "@tanstack/react-router";
import { CharacterCard } from "@web/components/CharacterCard";
import { ManageCharactersView } from "@web/components/ManageCharacterView";
import { useLinkCharacter } from "@web/route-services/chat/settings/use-link-character";
import { useUnlinkCharacter } from "@web/route-services/chat/settings/use-unlink-character";
import { useChatCharactersQuery } from "@web/route-services/chat/use-chat-characters-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { toast } from "sonner";

interface Props {
  chatID: number;
}

export function ManageCharactersModal({ chatID }: Props) {
  const { createModal } = useAppContext();
  const nav = useNavigate();
  const { data: chatCharactersData } = useChatCharactersQuery(chatID);
  const chatCharacters = Array.from(chatCharactersData?.values() ?? []);
  const linkCharacter = useLinkCharacter(chatID);
  const unlinkCharacter = useUnlinkCharacter(chatID);

  const getCharacterActions = (char: Character) => {
    return [
      {
        label: "Go To Page",
        icon: ArrowTopRightOnSquareIcon,
        onSelect: () => nav({ to: `/character/${char.id}` })
      },
      {
        label: "View",
        icon: EyeIcon,
        onSelect: () => {
          createModal({
            content: <CharacterCard char={char} />
          });
        }
      },
      {
        label: "Kick",
        icon: MinusCircleIcon,
        onSelect: () => handleRemoveCharacter(char),
        className: "text-danger"
      }
    ];
  };

  const handleCharacterSelect = (char: Character) => {
    if (chatCharacters.length >= chatConfig.maxCharacters) {
      toast.warning(`You can only have ${chatConfig.maxCharacters} characters in a chat.`);
      return;
    }
    linkCharacter.mutate(char.id);
  };

  const handleRemoveCharacter = (char: Character) => {
    if (chatCharacters.length === 1) {
      toast.warning("You must have at least one character in a chat.");
      return;
    }
    unlinkCharacter.mutate(char.id);
  };

  return (
    <ManageCharactersView
      charactersInChat={chatCharacters}
      onCharacterAdd={handleCharacterSelect}
      maxCharacters={chatConfig.maxCharacters}
      title="Manage Characters"
      description="Add or remove characters from your chat (ﾉ◕ヮ◕)ﾉ*:･ﾟ✧"
      getCharacterActions={getCharacterActions}
    />
  );
}
